import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { 
    EditorState, 
    ContentState,
    convertToRaw
} from 'draft-js';
import moment from 'moment-timezone'
import { motion, AnimateSharedLayout } from 'framer-motion'

import { Animation } from 'components'
import { ItemBlock, InputBlock, SectionBlock } from '../../components'

import { itemContext } from 'context'

const Container = styled.div`
    height: 100vh;
    width: 100%;
    background-color: ${props => props.backgroundColor};
    /* border: 1px solid greenyellow; */
`

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`

const Layout = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    height: 100%;
    width: 100%;
`

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: ${props => props.layout};
    grid-column-gap: 0.5rem;
    /* height: 100%; */
    width: ${props => props.width};
    justify-content: center;
`

const KanbanPreview = props => {
    const {
        progress
    } = props

    let items = []
    const timezone = moment.tz.guess();

    /////////////////
    // Controllers //
    /////////////////
    const getItem = (item, animateProps) => {
        let updatedItem = Object.assign({}, {
            content: '',
            priority: itemContext.PRIORITY.NONE,
            status: itemContext.STATUS.INCOMPLETE,
            offset: 0
        }, {...item})
        updatedItem['content'] = JSON.stringify(convertToRaw(EditorState.createWithContent(ContentState.createFromText(item['content'])).getCurrentContent()))
        return (
            <motion.div key={item['content']} layout>
                <div style={{display: 'flex', justifyContent:'flex-end'}}>
                    <ItemBlock item={updatedItem} animateProps={animateProps}/>
                </div>
            </motion.div>
        )
    }

    //////////////////
    // Create items //
    //////////////////
    let sections = []
    let s1 = [], s2 = [], s3 = [], s4 = []

    // Section 1 //
    s1.push(<motion.div layout><SectionBlock title='📝 To do'/></motion.div>)
    const item_s11 = getItem({
        content: 'Prepare slides for weekly update',
        priority: progress > 0.20 ? itemContext.PRIORITY.MEDIUM : itemContext.PRIORITY.NONE,
        start_date: progress > 0.60 && moment().tz(timezone),
        end_date: progress > 0.60 && moment().tz(timezone),
        start_time: progress > 0.60 && moment().tz(timezone).set({hour: 8, minute: 0}),
        end_time: progress > 0.60 && moment().tz(timezone).set({hour: 9, minute: 0}),
    })
    const item_s13 = getItem({content: 'Draft feature request for autoblock functionality', tags: progress > 0.60 && [{content: 'Request', color: '#4d908e'}]}, true)
    const item_s12 = getItem({
        content: 'Meeting with John on Q2 priorities', 
        priority: progress > 0.20 ? itemContext.PRIORITY.LOW : itemContext.PRIORITY.NONE,
        start_date: progress > 0.60 && moment().tz(timezone),
        end_date: progress > 0.60 && moment().tz(timezone),
        start_time: progress > 0.60 && moment().tz(timezone).set({hour: 10, minute: 0}),
        start_time: progress > 0.60 && moment().tz(timezone).set({hour: 11, minute: 0}),
    }, true)
    const s1_items = progress < 0.40 ? [item_s12, item_s11, item_s13] 
                    : [item_s11, item_s12, item_s13]
    s1.push(...s1_items)
    sections.push(s1)

    // Section 2 //
    s2.push(<motion.div layout><SectionBlock title='🚧  In progress'/></motion.div>)
    const item_s21 = getItem({
        content: 'Revamped design for integrations feature',
        priority: progress > 0.20 ? itemContext.PRIORITY.HIGH : itemContext.PRIORITY.NONE,
        start_date: moment().tz(timezone),
        end_date: moment().tz(timezone),
        start_time: moment().tz(timezone).set({hour: 14, minute: 0}),
        end_time: moment().tz(timezone).set({hour: 16, minute: 0})
    })
    const item_s22 = getItem({content: 'Google Calendar integration'})
    const item_s23 = getItem({content: 'Zoom integration'}, true)
    const item_s24 = getItem({content: 'Gmail integration'})
    const item_s25 = getItem({content: 'Google meetings integration'})
    const s2_items = progress < 0.40 ? [item_s22, item_s21, item_s23, item_s24, item_s25] 
                    : [item_s21, item_s25, item_s24, item_s22, item_s23]
    s2.push(...s2_items)
    sections.push(s2)

    // Section 3 //
    s3.push(<motion.div layout><SectionBlock title='👀  Backlog'/></motion.div>)
    const item_s31 = getItem({content: 'Landing screen banner re-design', tags: progress > 0.60 && [{content: 'Figma', color: '#ea4861'}]}, true)
    const item_s32 = getItem({content: 'Improvements for rich text editor'})
    const s3_items = [item_s31, item_s32] 
    s3.push(...s3_items)
    sections.push(s3)

    // Section 4 //
    s4.push(<motion.div layout><SectionBlock title='👾  Bugs'/></motion.div>)
    const item_s41 = getItem({
        content: 'Rate limit not supported on app v2.65', 
        priority: progress > 0.20 ? itemContext.PRIORITY.MEDIUM : itemContext.PRIORITY.NONE, 
        children: progress > 0.60 && {completed: 0, total: 2}, 
        start_date: progress > 0.60 && moment().tz(timezone),
        end_date: progress > 0.60 && moment().tz(timezone),
    }, true)
    const item_s44 = getItem({content: 'Wrong translation for change email warning'})
    const item_s45 = getItem({
        content: 'Sign out button not working', 
        priority: progress > 0.20 ? itemContext.PRIORITY.HIGH : itemContext.PRIORITY.NONE, 
        start_date: progress > 0.60 && moment().tz(timezone),
        end_date: progress > 0.60 && moment().tz(timezone),
    }, true)
    const s4_items = progress < 0.40 ? [item_s44, item_s41, item_s45] 
                    : [item_s45, item_s41, item_s44]
    s4.push(...s4_items)
    sections.push(s4)
    
    if(window.innerWidth <= 1000){
        sections = sections.slice(0,2)
    }

    return (
        <Container backgroundColor={progress < 0.10 ? '#2B2B2B' : 'transparent'}>
            {/* KanbanPreview {progress} */}
            <Layout>
                {progress > 0.60 ?
                <Wrapper width={progress < 0.10 & window.innerWidth <= 1000 ? '100%' : progress < 0.10 ? '25%' :'100%'} layout={progress < 0.10 ? '100%' : window.innerWidth <= 1000 ? '50% 50%' : '25% 25% 25% 25%'}>
                    {sections.map(section => (
                        <motion.div layout>
                            {progress < 0.10 ? section.slice(1,section.length) : section}
                        </motion.div>
                    ))}
                </Wrapper>
            : <AnimateSharedLayout type='crossfade'>
                <Wrapper width={progress < 0.10 & window.innerWidth <= 1000 ? '100%' : progress < 0.10 ? '25%' :'100%'} layout={progress < 0.10 ? '100%' : window.innerWidth <= 1000 ? '50% 50%' : '25% 25% 25% 25%'}>
                    {sections.map(section => (
                        <motion.div layout>
                            {progress < 0.10 ? section.slice(1,section.length) : section}
                        </motion.div>
                    ))}

                </Wrapper>
            </AnimateSharedLayout>}
            </Layout>

        </Container>
    )
}

KanbanPreview.propTypes = {
    progress: PropTypes.number
}

export default KanbanPreview